import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

const INITIAL_DELAY = 3000; // 3 seconds before showing
const DISPLAY_DURATION = 6000; // 6 seconds total display

export const SocialProofPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Generate consistent count for the week (20-40 range)
  const weekSeed = Math.floor(Date.now() / (7 * 24 * 60 * 60 * 1000));
  const count = 20 + (weekSeed % 21); // 20-40

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    
    checkMobile();
    window.addEventListener("resize", checkMobile, { passive: true });
    
    const showTimer = setTimeout(() => {
      setIsVisible(true);
    }, INITIAL_DELAY);

    return () => {
      clearTimeout(showTimer);
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  useEffect(() => {
    if (!isVisible || isDismissed) return;

    const hideTimer = setTimeout(() => {
      setIsVisible(false);
    }, DISPLAY_DURATION);

    return () => clearTimeout(hideTimer);
  }, [isVisible, isDismissed]);

  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);
  };

  if (isDismissed) return null;

  // Hide on mobile
  if (isMobile || isDismissed) return null;

  return (
    <div
      className={cn(
        "fixed z-50 bottom-5 left-5 max-w-[300px]",
        "transition-all duration-500 ease-out",
        isVisible
          ? "translate-y-0 opacity-100"
          : "translate-y-4 opacity-0 pointer-events-none"
      )}
      role="status"
      aria-live="polite"
    >
      <div className="group relative bg-card/95 backdrop-blur-xl border border-border/70 rounded-2xl shadow-[0_8px_30px_-8px_hsl(var(--foreground)/0.12)] pl-3 pr-2.5 py-3 flex items-center gap-3 hover:shadow-[0_12px_40px_-8px_hsl(var(--foreground)/0.18)] hover:-translate-y-0.5 transition-all duration-300">
        {/* Avatar stack */}
        <div className="flex -space-x-2 flex-shrink-0">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className={cn(
                "h-8 w-8 rounded-full border-2 border-card flex items-center justify-center font-sans text-[10px] font-semibold text-primary-foreground",
                i === 0 && "bg-primary",
                i === 1 && "bg-primary/80",
                i === 2 && "bg-primary/60"
              )}
            >
              {String.fromCharCode(65 + i)}
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-0.5 min-w-0 flex-1">
          <div className="flex items-baseline gap-1.5">
            <span className="font-sans text-[15px] font-semibold text-foreground tracking-[-0.01em] leading-none tabular-nums">
              {count}
            </span>
            <span className="font-sans text-[13px] text-foreground/75 leading-none">
              teams this week
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-primary opacity-60 animate-ping" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
            </span>
            <span className="font-sans text-[11px] text-muted-foreground leading-none">
              Building better feedback loops
            </span>
          </div>
        </div>

        <button
          onClick={handleDismiss}
          className="p-1.5 rounded-lg hover:bg-muted transition-colors flex-shrink-0 text-muted-foreground/60 hover:text-foreground self-start -mr-0.5 -mt-0.5"
          aria-label="Dismiss notification"
        >
          <X className="h-3 w-3" strokeWidth={2} />
        </button>
      </div>
    </div>
  );
};
